import { useMemo, useEffect } from "react";
import * as React from "react";
import { useTable, useFilters, usePagination, useSortBy, type TableState } from "react-table";
import { translate } from "../IntlProviderWrapper";
import Loader from "../Loader";
import DateTimePicker from "../DateTimePicker";
import ErrorComponent from "../ErrorComponent";
import DefaultPagination from "./Pagination";

import type { JSX } from "react";
import type { Props as PaginationProps } from "./Pagination";
import type { TableInstance, Cell, Row, Column } from "react-table";

type DateFilterType = (rows: Row[], id: string[], filterValue: string) => Row[];
type MultiSelectFilterType = (rows: Row[], id: string[], filterValues: string[]) => Row[];
type RangeFilterType = (rows: Row[], id: string, [min, max]: [number, number]) => Row[];

export type ReactTableColumn<T> = {
    Header: string | React.ReactNode | ((props: TableInstance) => React.ReactNode);
    accessor: keyof T;
    textAlign?: "left" | "right" | "center";
    filterInputPlaceholder?: string;
    filter?: DateFilterType | RangeFilterType | MultiSelectFilterType;
    Filter?: (props: Column<T>) => JSX.Element;
    disableFilters?: boolean;
    enableSorting?: boolean;
    sortType?: string | ((row_a: Row<T>, row_b: Row<T>, column_id: string) => number);
    Cell?: (props: TableInstance) => React.ReactNode;
    title?: string;
    override_default_td_cell?: boolean;
    fix?: boolean;
    class_name?: string;
    filter_type?: string;
    DateFilter?: React.ComponentType<DateFilterProps>;
    id?: string;
    width?: string;
    minWidth?: number;
};

type Filter = {
    filterValue?: string;
    setCustomFilter: (value: string, setFilter: (value: string) => void) => void;
};

type Filters = {
    [key: string]: Filter;
};

type Props<T> = {
    data: T[];
    columns: ReactTableColumn<T>[];
    onCellClick?: Function;
    hiddenColumns?: string[];
    isLoading?: boolean;
    onChangeCell?: Function;
    className?: string;
    rowStyle?: Function;
    rowClassName?: (row: Row<T>) => string | string[];
    filters?: Filters;
    ExpandableRow?: React.ComponentType<{ row: Row<T> }>;
    LastRow?: React.ComponentType<{ rows: Row<T> }>;
    isSticky?: boolean;
    table_id?: string;
    small_loader?: boolean;
    initialState?: Partial<TableState>;
    maxRows?: number;
    filter_type?: "date";
    getCellProps?: Function;
    Pagination?: React.ComponentType<PaginationProps> | boolean;
    error_msg?: string;
    hideHeader?: boolean;
};

type FilterInputProps<T> = {
    filters: Filters;
    column: Column<T>;
    initial_value: string;
    setFilter: (value: string) => void;
};
type FilterInputState = {
    original_value: string;
};

class FilterInput<T> extends React.Component<FilterInputProps<T>, FilterInputState> {
    constructor(props: FilterInputProps<T>) {
        super(props);
        this.state = {
            original_value: props.initial_value || "",
        };
    }

    componentDidUpdate(prev_props) {
        const filter_value = this.props.column.filterValue || "";
        const { original_value } = this.state;
        if (filter_value !== prev_props.column.filterValue && filter_value !== original_value) {
            this.setState({
                original_value: filter_value,
            });
        }
    }

    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const column = this.props.column;
        const filters = this.props.filters;

        if (e.target instanceof HTMLInputElement) {
            const target_value = e.target.value;
            this.setState({ original_value: target_value });

            const value = target_value.trim();
            if (filters[column.id]) {
                filters[column.id].setCustomFilter(value, this.props.setFilter);
            }

            this.props.setFilter(value); // Set undefined to remove the filter entirely
        }
    };

    render() {
        // this component manages trimmed spaces
        const column = this.props.column;
        const filters = this.props.filters;
        const filterValue = (filters[column.id] && filters[column.id].filterValue) || "";

        // original_value is set via initial_value which can be used to open modal dialog with preapplied filters
        // (eg order id) to show only specific orders
        // filterValue is used so that outer components using ReactTable can control the filter value via Redux or some other way

        return (
            <input
                value={this.state.original_value || filterValue}
                className="form-control column-filter"
                onChange={this.onChange}
                title={column.filterInputPlaceholder}
                placeholder={column.filterInputPlaceholder || translate("common.search", "Search")}
            />
        );
    }
}

export type DateFilterProps = {
    column: Column;
    filters: Filters;
    popupParent?: HTMLElement | null;
    setFilter: (column_id: string, value?: string) => void;
};

type DateFilterState = {};

export class DateFilter extends React.Component<DateFilterProps, DateFilterState> {
    onChange = (date: Date) => {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        this.props.setFilter(this.props.column.id, date.getTime() + "");
    };

    getFilterValue = (): Date | undefined => {
        const { filterValue } = this.props.column;
        const timestamp = parseInt(filterValue);
        if (Number.isNaN(timestamp)) {
            return undefined;
        }
        return new Date(timestamp);
    };

    onClear = () => {
        this.props.setFilter(this.props.column.id, undefined);
    };

    render() {
        const value = this.getFilterValue();

        return (
            <DateTimePicker
                inputClassName="column-filter"
                name="start_time"
                pickerType="date"
                value={value}
                popupPostion="fixed"
                popupParent={this.props.popupParent}
                title={this.props.column.filterInputPlaceholder}
                placeholder={this.props.column.filterInputPlaceholder || translate("common.date", "Date")}
                onClear={this.onClear}
                onChange={this.onChange}
            />
        );
    }
}

const DefaultColumnFilter = ({ column, filters, initialState }) => {
    let { setFilter } = column;

    useEffect(() => {
        if (column && filters[column.id] && filters[column.id].filterValue) {
            setFilter(filters[column.id].filterValue);
        }
    }, []);

    const initial_filter = initialState.filters && initialState.filters.find(filter => filter.id === column.id);

    return (
        <FilterInput
            key={column.id}
            column={column}
            filters={filters}
            initial_value={(initial_filter && initial_filter.value) || ""}
            setFilter={setFilter}
        />
    );
};

const generateSortingIndicator = (column, show_unsorted = false) => {
    if (!column.isSorted) {
        return show_unsorted ? <i className="fas fa-sort" /> : null;
    }

    return !column.isSortedDesc ? <i className="fas fa-sort-down" /> : <i className="fas fa-sort-up" />;
};

const getTableStyle = () => {
    return {
        position: "relative",
        borderCollapse: "collapse",
    };
};

const before_element_on_page = {
    "/manufacturing/stocks/stock_status": ".views-filter-bar",
    "/manufacturing/lines/subcontracting": ".views-filter-bar",
    "/manufacturing/stocks/stock_requirements": ".navigation-bar",
};

const getStickyTh = () => {
    let el: HTMLElement | null = null;
    for (const [location, selector] of Object.entries(before_element_on_page)) {
        if (window.location.href.includes(location)) {
            // $FlowFixMe
            el = document.querySelector(selector);
        }
    }

    const style = {
        background: "white",
        position: "sticky",
        top: "",
    };

    const is_modal = document.querySelector("body.modal-open");
    if (is_modal) {
        style.top = "0px";
    } else if (el && !is_modal) {
        style.top = el.offsetHeight + el.offsetTop + "px";
    }

    return style;
};

function ReactTable<T>(props: Props<T>): JSX.Element {
    const {
        data,
        columns,
        initialState,
        onCellClick = (cell: Cell<T>) => cell,
        hiddenColumns,
        filters = {},
        onChangeCell = null,
        rowStyle = () => {},
        rowClassName = () => null,
        ExpandableRow = null,
        table_id = "",
        LastRow = null,
        maxRows,
        getCellProps = () => ({}),
        Pagination = null,
        hideHeader = false,
    } = props;

    let isSticky: boolean = props.isSticky || true;
    if (props.className && props.className.includes("tags-react-table")) {
        isSticky = false;
    }

    const defaultColumn = useMemo(
        () => ({
            Filter: props => {
                return <DefaultColumnFilter filters={filters} {...props} />;
            },
        }),
        [filters]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows: all_rows,
        prepareRow,
        page: page_rows,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState,
            defaultColumn,
            NoDataComponent: () => "No data",
            noDataText: <span>no data</span>,
        },
        useFilters,
        useSortBy,
        usePagination
    );

    if (props.isLoading) {
        return <Loader small={props.small_loader} />;
    }

    const rows = Pagination ? page_rows : all_rows;
    const pagination_props: PaginationProps = {
        num_rows: all_rows.length,
        can_previous_page: canPreviousPage,
        can_next_page: canNextPage,
        page_options: pageOptions,
        page_count: pageCount,
        page_index: pageIndex,
        page_size: pageSize,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
    };
    const numHeaders = headerGroups && headerGroups[0].headers && headerGroups[0].headers.length;
    const className = `table react-table ${props.className || ""}`;
    const tableStyle = isSticky ? getTableStyle() : {};

    return (
        <>
            <table id={table_id} className={className} {...getTableProps()} style={tableStyle}>
                {!hideHeader && (
                    <thead>
                        {headerGroups.map((headerGroup, group_index) => (
                            <tr key={group_index} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => {
                                    if (hiddenColumns && hiddenColumns.includes(column.id)) return null;

                                    let sorting_props = {};
                                    if (column.enableSorting) {
                                        sorting_props = column.getSortByToggleProps();
                                    }

                                    const { onClick, style, ...header_props } = column.getHeaderProps(sorting_props);
                                    const th_style = {
                                        ...(style || {}),
                                        ...(isSticky ? getStickyTh() : {}),
                                    };

                                    if (!column.canFilter && onClick) {
                                        header_props.onClick = onClick;
                                    }

                                    if (column.title !== undefined) {
                                        header_props.title = column.title;
                                    } else if (column.enableSorting) {
                                        header_props.title = column.canFilter
                                            ? ""
                                            : translate("common.toggle_sorting", "Toggle sorting");
                                    }

                                    if (th_style.cursor && column.canFilter) {
                                        th_style.cursor = "default";
                                    }

                                    return (
                                        <th key={column.id} {...header_props} name={column.id} style={th_style}>
                                            {column.render("Header")}
                                            {column.enableSorting && !column.canFilter && (
                                                <> {generateSortingIndicator(column, true)}</>
                                            )}
                                            {/* Render the columns filter UI */}
                                            {column.canFilter && (
                                                <div className="d-flex align-items-center">
                                                    {column.filter_type === "date"
                                                        ? column.render("DateFilter", filters || {})
                                                        : column.render("Filter", { filters })}
                                                    {column.enableSorting && (
                                                        <span
                                                            className="p-2"
                                                            onClick={onClick}
                                                            style={{ cursor: "pointer" }}
                                                            title={translate("common.toggle_sorting", "Toggle sorting")}
                                                        >
                                                            {generateSortingIndicator(column, true)}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                )}
                {rows.length === 0 || data.length === 0 ? (
                    <tbody>
                        <tr>
                            <td colSpan={numHeaders}>
                                <ErrorComponent
                                    type="no-data"
                                    msg={props.error_msg || translate("common.no_data", "No data")}
                                />
                            </td>
                        </tr>
                    </tbody>
                ) : (
                    <tbody {...getTableBodyProps()}>
                        {rows.slice(0, maxRows ? maxRows : undefined).map((row, i) => {
                            prepareRow(row);
                            let style = rowStyle(row);
                            let className = rowClassName(row);
                            return (
                                <React.Fragment key={"rf_rt" + i}>
                                    <tr
                                        {...row.getRowProps()}
                                        className={className}
                                        style={{ ...style }}
                                        key={"rt" + i}
                                    >
                                        {row.cells.map((cell, j) => {
                                            if (hiddenColumns && hiddenColumns.includes(cell.column.id)) return null;
                                            let className: string | null = null;
                                            const column = columns[j];
                                            if (column.textAlign !== undefined) {
                                                className =
                                                    column.textAlign === "right"
                                                        ? "right"
                                                        : column.textAlign === "center"
                                                          ? "center"
                                                          : null;
                                            }

                                            if (column.override_default_td_cell) {
                                                return cell.render("Cell", { onChangeCell, cell });
                                            }

                                            return (
                                                <td
                                                    {...cell.getCellProps([
                                                        {
                                                            style: {
                                                                maxWidth: "200px",
                                                                wordWrap: "break-word",
                                                            },
                                                            className,
                                                        },
                                                        getCellProps(cell),
                                                    ])}
                                                    name={cell.column.id}
                                                    onClick={() => onCellClick && onCellClick(cell)}
                                                    key={`rtc_${i}_${j}`}
                                                >
                                                    {cell.render("Cell", { onChangeCell })}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {ExpandableRow && <ExpandableRow row={row} />}
                                </React.Fragment>
                            );
                        })}
                        {LastRow && <LastRow rows={rows} />}
                    </tbody>
                )}
            </table>
            {Pagination === true ? (
                <DefaultPagination {...pagination_props} />
            ) : typeof Pagination === "function" ? (
                <Pagination {...pagination_props} />
            ) : null}
        </>
    );
}

export default ReactTable;
